/**
 * Main Layout Component (shadcn-admin style)
 * Floating sidebar with compact content area
 */

import React, { useState } from 'react'
import { AppSidebar } from './AppSidebar'
import { Breadcrumbs } from '@/components/navigation/Breadcrumbs'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)
  const closeSidebar = () => setIsSidebarOpen(false)
  const toggleCollapse = () => setIsCollapsed(!isCollapsed)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Sidebar */}
      <AppSidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapse}
      />

      {/* Main Content Area */}
      <div className={`
        min-h-screen
        transition-all duration-300
        ${isCollapsed ? 'lg:ml-[4.5rem]' : 'lg:ml-[17rem]'}
      `}>
        {/* Mobile header with menu button */}
        <div className="lg:hidden sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 h-14 flex items-center justify-between shadow-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="p-2"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center space-x-2">
            <img
              src="/pao-logo.svg"
              alt="PAO Logo"
              className="w-8 h-8"
            />
            <span className="text-sm font-bold text-gray-900 dark:text-white">PAO System</span>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>

        {/* Main Content - Compact and clean */}
        <main className="p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
